import React, { useEffect, useState } from "react";
import {
  AddButton,
  Body,
  CardContent,
  CardMeta,
  CardTitle,
  CloseButton,
  Container,
  Deadline,
  DeadlineDate,
  Description,
  HeaderInfo,
  MetaIcon,
  MetaItem,
  Modal,
  ModalContent,
  PaginationWrapper,
  PlusIcon,
  ViewCard,
  ViewCardContent,
  ViewList,
  Wrapper,
} from "../style/view.styles";
import Pagination from "../components/Elements/Pagination";
import { fetchEntityData } from "../utils/RequestHandler";
import EmptyState from "../components/Elements/EmptyState";
import LoadingSpinner from "../components/Elements/LoadingSpinner";
import InternshipApplicationForm from "../components/Elements/InternshipApplicationForm";
import ConfirmCancelInternshipModal from "../components/Elements/ConfirmCancelInternshipModal";
import FilterSidebar from "../components/Elements/FilterSidebar";
import { useNavigate } from "react-router-dom";
import AddInternshipForm from "../components/Elements/AddInternshipForm";
import { FiCalendar, FiHome } from "react-icons/fi";

const filters = [
  {
    filter: "Work Mode",
    options: ["Remote", "On-site", "Hybrid", "Flexible"],
  },
  {
    filter: "Compensation Type",
    options: ["Paid", "Benefit In Kind", "Unpaid"],
  },
];

export default function Internships() {
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCancelOpen, setIsModalCancelOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isProtected, setIsProtected] = useState(null);

  const navigate = useNavigate();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleModalBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseAddModal();
    }
  };

  const recordsPerPage = 5;
  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const user = localStorage.getItem("user");
      if (user) {
        const parsedUser = JSON.parse(user);
        const role = parsedUser.role;
        setIsLoggedIn(true);
        setUserRole(role);
        setIsProtected(role === "organization");
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
        setIsProtected(false);
      }
    };

    checkUserLoggedIn();
  }, []);

  useEffect(() => {
    if (isLoggedIn !== null && isProtected !== null) {
      const fetchInternships = async () => {
        try {
          const isProtected = isLoggedIn && userRole === "organization";
          const data = await fetchEntityData("internships", isProtected);
          if (data.success) {
            const records = data.result;
            setFilteredInternships(records);
          }
        } catch (error) {
          console.error("Failed to fetch internships:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchInternships();
    }
  }, [isLoggedIn, isProtected, userRole]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredInternships.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const handlePageChange = (page) => setCurrentPage(page);
  const handleModalClose = () => setIsModalOpen(false);
  const handleCancelModalClose = () => setIsModalCancelOpen(false);
  const handleSelectInternship = (internship) => {
    setSelectedInternship(internship);
    navigate("/internships-detail", {
      state: { internship },
    });
  };

  return (
    <>
      <Wrapper className="paleBlueGreyBg">
        <Container>
          {/* Sidebar Section */}
          <FilterSidebar filters={filters} />

          {/* Body Section */}
          <Body>
            <HeaderInfo>
              <h1 className="font30 darkGreenColor extraBold">Internships</h1>
              {isLoggedIn && userRole === "organization" && (
                <AddButton onClick={handleOpenAddModal}>
                  <PlusIcon />
                  <span>Add New</span>
                </AddButton>
              )}
            </HeaderInfo>

            {loading && <LoadingSpinner />}
            {!loading && filteredInternships && (
              <div>
                <ViewList>
                  {currentRecords.map((record, index) => (
                    <ViewCard
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectInternship(record);
                      }}
                    >
                      <ViewCardContent>
                        <CardContent>
                          <CardTitle>{record.position}</CardTitle>
                          <Description>
                            {`${record.description.slice(0, 50)}${
                              record.description.length > 50 ? "..." : ""
                            }`}
                          </Description>
                          <CardMeta>
                            <MetaItem>
                              <MetaIcon>
                                <FiHome />
                              </MetaIcon>
                              <span>{record.organization}</span>
                            </MetaItem>
                            <Deadline>
                              <MetaIcon>
                                <FiCalendar />
                              </MetaIcon>
                              <span>Start Date: </span>
                              <DeadlineDate>
                                {new Date(record.start_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                              </DeadlineDate>
                            </Deadline>
                          </CardMeta>
                        </CardContent>
                      </ViewCardContent>
                    </ViewCard>
                  ))}
                </ViewList>
              </div>
            )}
            {!loading && filteredInternships?.length === 0 && (
              <EmptyState message={"Internships soon to come."} />
            )}
            {filteredInternships.length > recordsPerPage ? (
              <PaginationWrapper>
                <Pagination
                  totalItems={filteredInternships.length}
                  itemsPerPage={recordsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </PaginationWrapper>
            ) : (
              ""
            )}
          </Body>
        </Container>
      </Wrapper>
      {isAddModalOpen && (
        <Modal onClick={handleModalBackgroundClick}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton></CloseButton>
            <AddInternshipForm
              handleModalClose={handleCloseAddModal}
              onSuccess={() => {
                handleCloseAddModal();
                setLoading(true);
                fetchEntityData("internships", isProtected).then((data) => {
                  if (data.success) {
                    setFilteredInternships(data.result);
                  }
                  setLoading(false);
                });
              }}
            />
          </ModalContent>
        </Modal>
      )}
      {isModalOpen && (
        <Modal>
          <InternshipApplicationForm
            selectedInternship={selectedInternship}
            handleModalClose={handleModalClose}
          />
        </Modal>
      )}
      {isModalCancelOpen && (
        <ConfirmCancelInternshipModal
          selectedInternship={selectedInternship}
          handleModalClose={handleCancelModalClose}
        />
      )}
    </>
  );
}
